import { useState, useEffect } from 'react';
import { supabase, Application, ApplicationAnswer } from '../lib/supabase';
import {
  ArrowLeft, Mail, Phone, FileText, Calendar, CheckCircle,
  ExternalLink, ChevronDown, MessageSquare,
} from 'lucide-react';
import Messaging from './Messaging';

const STATUS_CONFIG = {
  new: { label: 'New', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  reviewing: { label: 'Reviewing', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  interview: { label: 'Interview', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  hired: { label: 'Hired', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  rejected: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-600', dot: 'bg-red-500' },
} as const;

type Props = {
  applicationId: string;
  onBack: () => void;
  onStatusChange: (status: Application['status']) => void;
};

export default function ApplicantDetail({ applicationId, onBack, onStatusChange }: Props) {
  const [application, setApplication] = useState<Application | null>(null);
  const [answers, setAnswers] = useState<ApplicationAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [cvSignedUrl, setCvSignedUrl] = useState<string | null>(null);
  const [cvLoading, setCvLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [applicationId]);

  async function loadData() {
    setLoading(true);
    const [{ data: app }, { data: ans }] = await Promise.all([
      supabase.from('applications').select('*').eq('id', applicationId).maybeSingle(),
      supabase.from('application_answers').select('*').eq('application_id', applicationId).order('created_at'),
    ]);
    setApplication(app);
    setAnswers(ans ?? []);
    setLoading(false);
  }

  async function loadCvUrl() {
    if (!application?.cv_url || cvSignedUrl) return;
    setCvLoading(true);
    const { data, error } = await supabase.storage
      .from('cvs')
      .createSignedUrl(application.cv_url, 300);
    if (data?.signedUrl) {
      setCvSignedUrl(data.signedUrl);
    }
    if (error) {
      console.error('Failed to generate signed URL for CV:', error.message);
    }
    setCvLoading(false);
  }

  async function changeStatus(status: Application['status']) {
    if (!application) return;
    setUpdatingStatus(true);
    await supabase.from('applications').update({ status }).eq('id', applicationId);
    setApplication((a) => a ? { ...a, status } : a);
    onStatusChange(status);
    setStatusOpen(false);
    setUpdatingStatus(false);
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!application) return (
    <div className="text-center py-16">
      <p className="text-gray-500">Application not found.</p>
      <button onClick={onBack} className="mt-3 text-blue-600 hover:underline text-sm">Go back</button>
    </div>
  );

  const cfg = STATUS_CONFIG[application.status];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="flex-1" />
        {/* Status selector */}
        <div className="relative">
          <button
            onClick={() => setStatusOpen(!statusOpen)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${cfg.bg} ${cfg.text}`}
          >
            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
            {cfg.label}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {statusOpen && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-100 rounded-md shadow-xl z-10 overflow-hidden">
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => changeStatus(key as Application['status'])}
                  disabled={updatingStatus}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors ${application.status === key ? 'font-semibold' : ''}`}
                >
                  <span className={`w-2 h-2 rounded-full ${val.dot}`} />
                  {val.label}
                  {application.status === key && <CheckCircle className="w-3 h-3 ml-auto text-green-500" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showMessages ? (
        <div className="bg-white border border-gray-200 rounded-lg p-5 h-[600px]">
          <Messaging application={application} onBack={() => setShowMessages(false)} />
        </div>
      ) : (
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: candidate info */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <span className="text-blue-700 font-bold text-lg">
                  {application.candidate_name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-bold text-gray-900">{application.candidate_name}</p>
                <p className="text-xs text-gray-500">Candidate</p>
              </div>
            </div>

            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <a href={`mailto:${application.candidate_email}`} className="text-sm text-blue-600 hover:underline truncate">
                  {application.candidate_email}
                </a>
              </div>
              {application.candidate_phone && (
                <div className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a href={`tel:${application.candidate_phone}`} className="text-sm text-gray-700">{application.candidate_phone}</a>
                </div>
              )}
              <div className="flex items-center gap-2.5">
                <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  Applied {new Date(application.submitted_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}
                </span>
              </div>
            </div>
          </div>

          {application.cv_url && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">CV / Resume</p>
              {cvSignedUrl ? (
                <a
                  href={cvSignedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                >
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700 flex-1 truncate">
                    {application.candidate_name}'s CV
                  </span>
                  <ExternalLink className="w-3.5 h-3.5 text-blue-500" />
                </a>
              ) : (
                <button
                  onClick={loadCvUrl}
                  disabled={cvLoading}
                  className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors w-full"
                >
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700 flex-1 truncate">
                    {cvLoading ? 'Generating secure link...' : `${application.candidate_name}'s CV`}
                  </span>
                  <ExternalLink className="w-3.5 h-3.5 text-blue-500" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right: screening answers + messaging */}
        <div className="lg:col-span-2 space-y-4">
          <button
            onClick={() => setShowMessages(true)}
            className="w-full flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors text-left"
          >
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-semibold text-blue-700 flex-1">Message {application.candidate_name.split(' ')[0]}</span>
            <span className="text-xs text-blue-500">Open chat</span>
          </button>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Screening Answers</p>
            {answers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No screening answers submitted.</p>
            ) : (
              <div className="space-y-4">
                {answers.map((ans) => (
                  <div key={ans.id} className="border-b border-gray-50 pb-4 last:border-0 last:pb-0">
                    <p className="text-sm font-semibold text-gray-700 mb-1">{ans.question_label}</p>
                    <p className={`text-sm ${ans.answer ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                      {ans.answer || 'No answer provided'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
